import React, { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ticketsApi } from '@/api/tickets.api';
import { Modal } from '@/components/ui/Modal';
import { Field } from '@/components/ui/Field';
import { ApiError } from '@/lib/errors';
import { useToasts } from '@/context/ToastContext';
import { CATEGORY_CODES, CategoryCode } from '@/types/domain';
import { CATEGORY_LABEL } from '@/types/domain';
import { invalidateAfterTicketAction } from '@/lib/queryInvalidation';
import { useTagTeammates } from './useTagTeammates';

interface Props {
  ticketId: string;
  currentCategory: string;
  open: boolean;
  onClose: () => void;
}

/**
 * V4.9.3 — Change a ticket's category mid-flight.
 *
 * Use case: customer raised an A4 (Replacement) ticket. During CX
 * conversation it became clear they want a refund. Click "Change category"
 * → pick A2 (Refund — shipped) → optionally tick "Re-route per new
 * category" to also update the assignees → Save.
 *
 * The current category is excluded from the dropdown so users can't
 * "change" to the same category and hit a 400.
 */
export function ChangeCategoryModal({ ticketId, currentCategory, open, onClose }: Props) {
  const [newCategory, setNewCategory] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const { push } = useToasts();
  const tag = useTagTeammates();

  useEffect(() => {
    if (open) {
      setNewCategory('');
      setReason('');
      setError(null);
    }
  }, [open]);

  const mutation = useMutation({
    // V4.9.4 — Always re-route to the new category's dedicated team.
    // No more optional flag; the backend re-routes on every category
    // change. (Ops feedback: forgetting to tick the box left tickets
    // stuck with the wrong owner/solver.)
    mutationFn: async () => {
      const res = await ticketsApi.changeCategory(ticketId, {
        new_category: newCategory,
        reason: reason || undefined
      });
      if (tag.hasContent) await ticketsApi.tag(ticketId, tag.payload());
      return res;
    },
    onSuccess: (data) => {
      const team = [
        data.owner?.name ? `Owner: ${data.owner.name}` : null,
        data.solver?.name ? `Solver: ${data.solver.name}` : null,
        data.communicator?.name ? `Communicator: ${data.communicator.name}` : null
      ].filter(Boolean).join(' · ');
      push({
        kind: 'success',
        title: `Category → ${data.category}`,
        message: team
          ? `Re-assigned: ${team}`
          : `Ticket moved to ${data.category} — ${CATEGORY_LABEL[data.category as CategoryCode] ?? data.category}.`
      });
      // V4.9.3 — invalidate ALL views so the badge at the top + every list
      // reflects the new category, solver, communicator, approver instantly.
      // Also pushes a 'ticket.updated' through the realtime channel from
      // the backend so other users see it too.
      invalidateAfterTicketAction(queryClient, ticketId);
      // Also write the response directly so the detail page header
      // re-renders without waiting for the refetch round-trip.
      queryClient.setQueryData(['ticket', ticketId], data);
      tag.reset();
      onClose();
    },
    onError: (err: unknown) => {
      setError(
        err instanceof ApiError ? err.problem?.detail ?? err.message : 'Failed to change category'
      );
    }
  });

  const otherCategories = CATEGORY_CODES.filter((c) => c !== currentCategory);
  const canSave = newCategory !== '' && !mutation.isPending;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Change category"
      footer={
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={!canSave}
            onClick={() => mutation.mutate()}
          >
            {mutation.isPending ? 'Saving...' : 'Change category'}
          </button>
        </>
      }
    >
      <div className="text-muted text-small" style={{ marginBottom: 12 }}>
        Current category: <strong>{currentCategory} — {CATEGORY_LABEL[currentCategory as CategoryCode] ?? currentCategory}</strong>
      </div>

      <Field label="New category" required>
        <select
          className="form-select"
          value={newCategory}
          onChange={(e) => setNewCategory(e.target.value)}
        >
          <option value="">Select a category…</option>
          {otherCategories.map((code) => (
            <option key={code} value={code}>
              {code} — {CATEGORY_LABEL[code] ?? code}
            </option>
          ))}
        </select>
      </Field>

      <Field label="Reason (optional)" hint="Why is the category changing? Shows up in the timeline.">
        <textarea
          className="form-textarea"
          rows={2}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="e.g. 'Customer asked for refund instead of replacement during the call.'"
        />
      </Field>

      {/* V4.9.4 — Re-routing is now AUTOMATIC. The ticket will be reassigned
          to the new category's dedicated team per the V4.9 routing matrix. */}
      <div className="auto-route-notice">
        <span className="auto-route-notice-icon" aria-hidden>🔄</span>
        <div>
          <div className="auto-route-notice-title">Auto-routing on save</div>
          <div className="text-small text-muted">
            Owner / Solver / Communicator will be automatically reassigned to the dedicated team for
            the new category per the routing matrix. After saving, the new team will receive an email
            notification immediately.
          </div>
        </div>
      </div>

      {tag.render()}
      {error ? <div className="form-error">{error}</div> : null}
    </Modal>
  );
}
